import { NonClientError, Status } from 'hooks/requests/fetchUtils';
import useFetchApi from 'hooks/useFetchApi';
import useFmcHost from 'hooks/useFmcHost';
import { useCallback, useState } from 'react';

import useQueryParameters from 'hooks/useQueryParameters';

import {
  UnifiedPrepareCheckoutResultType,
  receiveUnifiedPrepareCheckoutResult,
} from 'fmc-sdk';
import useIovation from 'hooks/useIovation';
import { Order, ShippingAddressType } from 'state/creditApplication';
import catchOnly from 'utils/catchOnly';
import useAnalytics, { Event } from 'hooks/useAnalytics';

const PATH = '/api/unified_prepare_checkout';

type UnifiedPrepareCheckoutHook = () => [
  (
    params: UnifiedPrepareCheckoutRequest,
  ) => Promise<UnifiedPrepareCheckoutResult>,
  Status,
  boolean,
];

export interface PartialError {
  error: true;
  code?: string;
  message?: string;
}

export interface UnifiedPrepareCheckoutRequest {
  firstName: string | null;
  lastName: string | null;
  emailAddress: string | null;
  mobilePhone: string | null;
  braintreeNonce?: string | null;
  braintreeCardType?: 'DEBIT' | 'CREDIT' | null;
  applicationId: string | undefined;
  paymentAgreementId: string | undefined;
  intendedPurchaseAmount: string | null;
  paymentProductType?: 'SPLITPAY' | 'INSTALLMENTS';
  disclosureChecksum: string | null;
  order: Order | null;
  shippingAddress: ShippingAddressType | null;
}

export interface UnifiedPrepareCheckoutResult {
  status: string;
  statusCodes: string[];
}

export class UnifiedPrepareCheckoutError extends Error {}

const useUnifiedPrepareCheckout: UnifiedPrepareCheckoutHook = () => {
  const [error, setError] = useState<boolean>(false);
  const [status, fetch] = useFetchApi();
  const { mockUPC, location, channel, subchannel } = useQueryParameters();
  const fmcDispatch = useFmcHost();
  const { sendEvents } = useAnalytics();
  const iovationBlackbox = useIovation();

  const unifiedPrepareCheckoutRequest = useCallback(
    async (
      params: UnifiedPrepareCheckoutRequest,
    ): Promise<UnifiedPrepareCheckoutResult> => {
      setError(false);

      const blackbox = await iovationBlackbox;

      try {
        const responsePromise = fetch(PATH, {
          method: 'POST',
          body: {
            ...params,
            iovationBlackbox: blackbox,
            mockResponse: mockUPC,
            location,
            channel,
            subchannel,
          },
        });
        sendEvents([Event.PrepareCheckoutSubmitted]);
        const response = await responsePromise;

        if (response.ok) {
          const result: UnifiedPrepareCheckoutResult = await response.json();
          if (result.status === 'CHECKOUT_PREPARED') {
            sendEvents([Event.CheckoutPrepared]);
            fmcDispatch(
              receiveUnifiedPrepareCheckoutResult({
                result: UnifiedPrepareCheckoutResultType.CheckoutPrepared,
              }),
            );
          } else {
            fmcDispatch(
              receiveUnifiedPrepareCheckoutResult({
                result: UnifiedPrepareCheckoutResultType.CheckoutNotPrepared,
              }),
            );
          }
          return Promise.resolve(result);
        } else {
          fmcDispatch(
            receiveUnifiedPrepareCheckoutResult({
              result: UnifiedPrepareCheckoutResultType.Error,
            }),
          );

          setError(true);
          return Promise.reject(new UnifiedPrepareCheckoutError());
        }
      } catch (e) {
        if (catchOnly(e, NonClientError)) {
          fmcDispatch(
            receiveUnifiedPrepareCheckoutResult({
              result: UnifiedPrepareCheckoutResultType.Error,
            }),
          );
        }
        // Re-throw so that any exceptions can be handled downstream.
        throw e;
      }
    },
    [
      iovationBlackbox,
      fetch,
      mockUPC,
      location,
      channel,
      subchannel,
      sendEvents,
      fmcDispatch,
    ],
  );

  return [unifiedPrepareCheckoutRequest, status, error];
};

export default useUnifiedPrepareCheckout;
